import { useMemo } from 'react';

import { styled } from '@mui/material';
import { deepNestedMerge, useTranslation } from '@kc-console/core';

import ModalFooterButton from './ModalFooterButton';
import {
  InitialModalFooterButtonVariantMapByEvent,
  ModalFooterContext,
} from './ModalFooterContext';
import { modalFooterClasses } from './theme';

import type { PropsWithChildren } from 'react';
import type { ModalFooterContextParams, ModalFooterProps } from './types';

const ModalFooter = (props: PropsWithChildren<ModalFooterProps>) => {
  const { children, buttons, loading = false, variantMap, labelMap } = props;
  const { t } = useTranslation();
  const initialLabelMap = useMemo(() => {
    return {
      cancel: t('common:common.action.cancel'),
      close: t('common:common.action.close'),
      submit: t('common:common.action.apply'),
      delete: t('common:common.action.delete'),
      confirm: t('common:common.action.confirm'),
    };
  }, [t]);

  const contextValues: ModalFooterContextParams = useMemo(() => {
    const refinedVariantMap = deepNestedMerge(
      InitialModalFooterButtonVariantMapByEvent,
      variantMap ?? {},
    );
    const refinedLabelMap = deepNestedMerge(initialLabelMap, labelMap ?? {});

    return {
      variantMap: refinedVariantMap,
      labelMap: refinedLabelMap,
      loading: loading,
    };
  }, [variantMap, labelMap, loading, initialLabelMap]);

  if ((!buttons || !buttons.length) && !children) return null;

  return (
    <ModalFooterContext.Provider value={contextValues}>
      <ModalFooterRoot>{!buttons ? children : buttons.map(button => button)}</ModalFooterRoot>
    </ModalFooterContext.Provider>
  );
};

ModalFooter.Button = ModalFooterButton;

export default ModalFooter;

const ModalFooterRoot = styled('div', {
  target: modalFooterClasses.root,
})`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  column-gap: 8px;
  padding: 24px 0 32px 0;
`;
